import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Reveal, Eyebrow } from "../components/ui";
import { API_BASE_URL, ENDPOINTS } from "../config/api";
import { formatMoney } from "../utils/format";
import {
  authFetch,
  safeJson,
  getUser,
  logoutUser,
  type AuthUser,
} from "../utils/auth";

interface Subscription {
  id?: string;
  planId?: string;
  planName?: string;
  plan?: {
    id?: string;
    name?: string;
    price?: number | string;
    currency?: string;
    durationDays?: number;
    features?: string[];
  };
  status?: string;
  startedAt?: string;
  startDate?: string;
  expiresAt?: string;
  endDate?: string;
  autoRenew?: boolean;
  [key: string]: any;
}

interface Profile {
  bio?: string;
  occupation?: string;
  age?: number;
  gender?: string;
  lookingFor?: string;
  datingIntent?: string;
  city?: string;
  interests?: string[];
  ethnicity?: string;
  photos?: { url: string }[];
  [key: string]: any;
}

interface Payment {
  id?: string;
  reference?: string;
  amount?: number | string;
  currency?: string;
  status?: string;
  channel?: string;
  createdAt?: string;
  paidAt?: string;
  [key: string]: any;
}

function unwrap<T = any>(json: any): T | null {
  if (!json) return null;
  if (json.data !== undefined) return json.data as T;
  return json as T;
}
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatDate(value?: string): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${d.getFullYear()}`;
}

export default function Account() {
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthUser | null>(getUser());

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [paymentsModal, setPaymentsModal] = useState<
    "history" | "failed" | null
  >(null);

  useEffect(() => {
    if (!localStorage.getItem("accessToken")) {
      navigate("/pay");
      return;
    }
    loadAll();
    // eslint-disable-next-line
  }, []);

  async function loadAll() {
    setLoading(true);
    setError("");

    async function tryGet(url: string) {
      try {
        const res = await authFetch(url, {}, 10000);
        const json = await safeJson(res);
        return { ok: res.ok, status: res.status, json };
      } catch (e: any) {
        const reason = e?.name === "AbortError" ? "timeout" : "network";
        return { ok: false, status: 0, json: null, error: reason };
      }
    }

    const [userR, profileR, subR, payR] = await Promise.all([
      tryGet(`${API_BASE_URL}${ENDPOINTS.USERS_ME}`),
      tryGet(`${API_BASE_URL}${ENDPOINTS.PROFILE_ME}`),
      tryGet(`${API_BASE_URL}${ENDPOINTS.SUBSCRIPTION_MY}`),
      tryGet(`${API_BASE_URL}${ENDPOINTS.PAYMENTS_MY}`),
    ]);

    if (userR.ok) {
      const u = unwrap<AuthUser>(userR.json);
      if (u) {
        setUser(u);
        localStorage.setItem("user", JSON.stringify(u));
      }
    }

    if (profileR.ok) setProfile(unwrap<Profile>(profileR.json));
    else setProfile(null);

    if (subR.ok) setSubscription(unwrap<Subscription>(subR.json));
    else setSubscription(null);

    if (payR.ok) {
      const j = payR.json;
      const arr =
        (Array.isArray(j) && j) ||
        (Array.isArray(j?.data) && j.data) ||
        (Array.isArray(j?.data?.items) && j.data.items) ||
        (Array.isArray(j?.items) && j.items) ||
        [];
      setPayments(arr as Payment[]);
    }

    const allFailed = !userR.ok && !profileR.ok && !subR.ok && !payR.ok;
    if (allFailed) {
      setError(
        "Could not load your account data. Check your connection or log in again.",
      );
    }

    setLoading(false);
  }

  async function handleCancelSubscription() {
    setCancelError("");
    setCancelling(true);
    try {
      const res = await authFetch(
        `${API_BASE_URL}${ENDPOINTS.SUBSCRIPTION_CANCEL}`,
        {
          method: "PATCH",
          body: JSON.stringify({ reason: cancelReason || "User cancelled" }),
        },
      );

      const j = await res.json().catch(() => null);

      if (!res.ok) {
        setCancelError(
          j?.message || j?.error || `Cancel failed (status ${res.status}).`,
        );
        setCancelling(false);
        return;
      }

      setShowCancelModal(false);
      setCancelReason("");
      await loadAll();
    } catch {
      setCancelError("Could not reach the server. Please try again.");
    } finally {
      setCancelling(false);
    }
  }

  function handleLogout() {
    logoutUser();
    navigate("/");
  }

  function initials(name?: string, email?: string) {
    if (name && name.trim()) {
      const parts = name.trim().split(/\s+/);
      return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
    }
    if (email) return email[0].toUpperCase();
    return "U";
  }

  function getAvatarUrl() {
    // 1. Check user photos (from /users/me)
    if (user?.photos && user.photos.length > 0) {
      return user.photos[0].url;
    }
    // 2. Check profile photos (from /profile/me)
    if (profile?.photos && profile.photos.length > 0) {
      return profile.photos[0].url;
    }
    return null;
  }

  const avatarUrl = getAvatarUrl();

  function statusColor(status?: string) {
    const s = (status || "").toUpperCase();
    if (s === "ACTIVE")
      return "bg-green-100 text-green-700 border border-green-200";
    if (s === "EXPIRED")
      return "bg-neutral-200 text-neutral-700 border border-neutral-300";
    if (s === "CANCELLED")
      return "bg-rose/15 text-rose-dark border border-rose/20";
    if (s === "PENDING")
      return "bg-amber-100 text-amber-700 border border-amber-200";
    return "bg-neutral-200 text-neutral-700 border border-neutral-300";
  }

  const failedPayments = payments.filter(
    (p) => (p.status || "").toUpperCase() === "FAILED",
  );
  const otherPayments = payments.filter(
    (p) => (p.status || "").toUpperCase() !== "FAILED",
  );

  function paymentRow(p: Payment, i: number) {
    const date = p.createdAt || p.paidAt;
    return (
      <div
        key={p.id || p.reference || i}
        className="flex items-center justify-between gap-3 px-4 md:px-5 py-3 border-b border-black/5 last:border-b-0"
      >
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-black">
            {formatMoney(p.amount || 0, p.currency || "MWK")}
          </p>
          <p className="text-[11px] md:text-[12px] text-muted capitalize truncate">
            {formatDate(date)}
            {p.channel ? ` · ${p.channel}` : ""}
          </p>
        </div>
        <span
          className={`inline-block flex-shrink-0 text-[9px] md:text-[10px] font-bold uppercase tracking-wider px-2 md:px-2.5 py-1 rounded-full ${statusColor(p.status)}`}
        >
          {p.status || "—"}
        </span>
      </div>
    );
  }

  if (loading) {
    return (
      <main className="bg-white px-[5%] pt-[140px] pb-20 min-h-[60vh]">
        <div className="max-w-[900px] mx-auto text-center py-16">
          <div className="w-10 h-10 border-2 border-black/10 border-t-rose rounded-full animate-spin mx-auto mb-6" />
          <p className="text-[15px] text-muted">Loading your account…</p>
        </div>
      </main>
    );
  }

  const activeSub =
    subscription && (subscription.status || "").toUpperCase() === "ACTIVE"
      ? subscription
      : null;

  const planName =
    subscription?.plan?.name ||
    subscription?.planName ||
    (typeof (subscription as any)?.plan === "string"
      ? (subscription as any).plan
      : "");

  const isPremium = planName.toLowerCase() === "premium";

  const currency = subscription?.plan?.currency || "MWK";
  const price = subscription?.plan?.price;
  const features = subscription?.plan?.features || [];
  const expiresAt = subscription?.expiresAt || subscription?.endDate;
  const startedAt = subscription?.startedAt || subscription?.startDate;

  return (
    <main className="bg-white px-[5%] pt-[88px] md:pt-[140px] pb-16 lg:pb-[100px]">
      <div className="max-w-[1100px] mx-auto">
        <Reveal>
          <Eyebrow>My Account</Eyebrow>
        </Reveal>

        {/* ---------- Header card ---------- */}
        <Reveal delay={50}>
          <div className="mt-3 mb-8 md:mb-10 p-4 md:p-8 rounded-2xl md:rounded-3xl bg-neutral-50 border border-black/5 flex flex-row flex-wrap items-center gap-3 md:gap-5">
            <div className="w-11 h-11 md:w-20 md:h-20 rounded-full bg-rose text-white font-bold text-base md:text-2xl flex items-center justify-center flex-shrink-0 overflow-hidden relative">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={user?.name || "Profile"}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                initials(user?.name, user?.email)
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg md:text-3xl font-bold tracking-tight text-black truncate">
                {user?.name || "User"}
              </h1>
              <p className="text-[12px] md:text-[14px] text-muted truncate">
                {user?.email || ""}
              </p>
              {user?.phone && (
                <p className="text-[11px] md:text-[13px] text-muted truncate">
                  {user.phone}{" "}
                  {user.phoneVerified && (
                    <span className="ml-1 text-[11px] text-green-600 font-medium">
                      ✓ Verified
                    </span>
                  )}
                </p>
              )}
            </div>
            <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
              <Link
                to="/my-chats"
                className="text-center w-full md:w-auto text-[12px] md:text-[13px] font-semibold text-white bg-black hover:bg-neutral-800 px-5 py-2 rounded-full transition-colors no-underline"
              >
                My Chats
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="w-full md:w-auto text-[12px] md:text-[13px] font-semibold text-muted hover:text-black border border-black/10 px-4 py-2 rounded-full transition-colors"
              >
                Log out
              </button>
            </div>
          </div>
        </Reveal>

        {error && (
          <div className="mb-8 p-4 rounded-2xl bg-blush border border-rose/20">
            <p className="text-[13px] text-rose-dark">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-8 lg:gap-8 items-start">
          <div className="space-y-8 md:space-y-10 min-w-0">
            {/* ---------- Subscription section ---------- */}
            <section>
              <Reveal delay={100}>
                <h2 className="text-base md:text-xl font-bold tracking-tight mb-3 md:mb-4">
                  Subscription
                </h2>
              </Reveal>

              {activeSub ? (
                <Reveal delay={125}>
                  <div className="p-4 md:p-7 rounded-2xl border border-black/10 bg-white">
                    {/* Top row: plan + status on the left, dates on the right */}
                    <div className="flex flex-row items-start justify-between gap-4 mb-5">
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-1">
                          Current plan
                        </p>
                        <div className="flex items-center gap-3">
                          <p className="text-xl md:text-2xl font-bold text-black">
                            {planName || "Subscription"}
                          </p>
                          {isPremium && (
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-amber-100 text-amber-700 rounded-full border border-amber-200 shadow-sm">
                              👑 Highest Tier
                            </span>
                          )}
                        </div>
                        {price !== undefined && (
                          <p className="text-[14px] text-muted mt-1">
                            {formatMoney(price, currency)}
                            {subscription?.plan?.durationDays
                              ? ` / ${subscription.plan.durationDays} days`
                              : ""}
                          </p>
                        )}
                        <span
                          className={`inline-flex mt-3 text-[10px] md:text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${statusColor(
                            subscription?.status,
                          )}`}
                        >
                          {subscription?.status || "ACTIVE"}
                        </span>
                      </div>

                      <div className="flex flex-col gap-3 flex-shrink-0">
                        {startedAt && (
                          <div>
                            <p className="text-[10px] md:text-[11px] uppercase tracking-wider text-muted font-semibold mb-0.5">
                              Started
                            </p>
                            <p className="text-[13px] md:text-[14px] text-black">
                              {formatDate(startedAt)}
                            </p>
                          </div>
                        )}
                        {expiresAt && (
                          <div>
                            <p className="text-[10px] md:text-[11px] uppercase tracking-wider text-muted font-semibold mb-0.5">
                              Renews / Expires
                            </p>
                            <p className="text-[13px] md:text-[14px] text-black">
                              {formatDate(expiresAt)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="border-t border-black/5 mb-5" />

                    {features.length > 0 && (
                      <div className="mb-6">
                        <p className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-3">
                          Your features
                        </p>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                          {features.map((f: string) => (
                            <li
                              key={f}
                              className="flex items-start gap-2 text-[13px] text-muted"
                            >
                              <svg
                                className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                              >
                                <polyline points="20,6 9,17 4,12" />
                              </svg>
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 md:gap-3">
                      {!isPremium ? (
                        <Link
                          to="/pay"
                          className="flex-1 sm:flex-none text-center bg-black text-white text-[12px] md:text-[13px] font-semibold px-4 md:px-5 py-2.5 rounded-full hover:bg-neutral-800 transition-colors no-underline"
                        >
                          Upgrade Plan
                        </Link>
                      ) : (
                        <div className="text-[13px] font-semibold text-amber-700 bg-amber-50 px-5 py-2.5 rounded-full border border-amber-200 flex items-center gap-2 cursor-default">
                          <span>✓</span> You are on our highest tier
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => setShowCancelModal(true)}
                        className="flex-1 sm:flex-none text-center text-[12px] md:text-[13px] font-semibold text-rose-dark border border-rose/30 px-4 md:px-5 py-2.5 rounded-full hover:bg-blush transition-colors"
                      >
                        Cancel subscription
                      </button>
                    </div>
                  </div>
                </Reveal>
              ) : (
                <Reveal delay={125}>
                  <div className="p-4 md:p-7 rounded-2xl border border-dashed border-black/15 bg-white text-center">
                    <p className="text-[15px] text-muted mb-5">
                      You don't have an active subscription yet.
                    </p>
                    <Link
                      to="/pay"
                      className="inline-block bg-magenta text-white text-sm font-semibold px-6 py-3 rounded-full hover:opacity-90 transition-opacity no-underline"
                    >
                      Choose a plan →
                    </Link>
                  </div>
                </Reveal>
              )}
            </section>

            {/* ---------- Recent payments ---------- */}
            <section>
              <Reveal delay={150}>
                <h2 className="text-base md:text-xl font-bold tracking-tight mb-3 md:mb-4">
                  Recent payments
                </h2>
              </Reveal>

              {payments.length > 0 ? (
                <Reveal delay={175}>
                  <div className="rounded-2xl border border-black/10 bg-white overflow-hidden">
                    {otherPayments.slice(0, 3).map(paymentRow)}

                    {otherPayments.length > 3 && (
                      <button
                        type="button"
                        onClick={() => setPaymentsModal("history")}
                        className="w-full flex items-center justify-between px-4 md:px-5 py-3 border-b border-black/5 last:border-b-0 text-[12px] md:text-[13px] font-semibold text-black hover:bg-neutral-50 transition-colors"
                      >
                        <span>View all {otherPayments.length} payments</span>
                        <svg
                          className="w-4 h-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    )}

                    {failedPayments.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setPaymentsModal("failed")}
                        className="w-full flex items-center justify-between px-4 md:px-5 py-3 border-b border-black/5 last:border-b-0 text-[12px] md:text-[13px] font-semibold text-rose-dark hover:bg-blush/40 transition-colors"
                      >
                        <span>
                          {failedPayments.length} failed{" "}
                          {failedPayments.length === 1 ? "payment" : "payments"}
                        </span>
                        <svg
                          className="w-4 h-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    )}
                  </div>
                </Reveal>
              ) : (
                <Reveal delay={175}>
                  <div className="p-4 md:p-6 rounded-2xl border border-dashed border-black/15 bg-white text-center">
                    <p className="text-[14px] text-muted">No payments yet.</p>
                  </div>
                </Reveal>
              )}
            </section>
          </div>

          {/* ================= RIGHT: profile + danger zone ================= */}
          <div className="space-y-8 md:space-y-10 min-w-0">
            <section>
              <Reveal delay={200}>
                <h2 className="text-base md:text-xl font-bold tracking-tight mb-3 md:mb-4">
                  Profile
                </h2>
              </Reveal>

              {profile && Object.keys(profile).length > 0 ? (
                <Reveal delay={225}>
                  <div className="p-4 md:p-6 rounded-2xl border border-black/10 bg-white">
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-5">
                      {profile.bio && (
                        <div className="col-span-2">
                          <dt className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-1">
                            Bio
                          </dt>
                          <dd className="text-[14px] text-black leading-relaxed">
                            {profile.bio}
                          </dd>
                        </div>
                      )}
                      {profile.occupation && (
                        <div>
                          <dt className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-1">
                            Occupation
                          </dt>
                          <dd className="text-[14px] text-black">
                            {profile.occupation}
                          </dd>
                        </div>
                      )}
                      {profile.age && (
                        <div>
                          <dt className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-1">
                            Age
                          </dt>
                          <dd className="text-[14px] text-black">
                            {profile.age}
                          </dd>
                        </div>
                      )}
                      {profile.gender && (
                        <div>
                          <dt className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-1">
                            Gender
                          </dt>
                          <dd className="text-[14px] text-black capitalize">
                            {profile.gender}
                          </dd>
                        </div>
                      )}
                      {profile.city && (
                        <div>
                          <dt className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-1">
                            City
                          </dt>
                          <dd className="text-[14px] text-black">
                            {profile.city}
                          </dd>
                        </div>
                      )}
                      {profile.lookingFor && (
                        <div className="col-span-2">
                          <dt className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-1">
                            Looking for
                          </dt>
                          <dd className="text-[14px] text-black">
                            {profile.lookingFor}
                          </dd>
                        </div>
                      )}
                      {Array.isArray(profile.interests) &&
                        profile.interests.length > 0 && (
                          <div className="col-span-2">
                            <dt className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-2">
                              Interests
                            </dt>
                            <dd className="flex flex-wrap gap-2">
                              {profile.interests.map((i: any) => {
                                const label =
                                  typeof i === "string"
                                    ? i
                                    : i?.name || i?.label || "";
                                return (
                                  <span
                                    key={label}
                                    className="text-[12px] bg-neutral-100 text-black px-3 py-1 rounded-full"
                                  >
                                    {label}
                                  </span>
                                );
                              })}
                            </dd>
                          </div>
                        )}
                    </dl>
                  </div>
                </Reveal>
              ) : (
                <Reveal delay={225}>
                  <div className="p-4 md:p-6 rounded-2xl border border-dashed border-black/15 bg-white text-center">
                    <p className="text-[13px] md:text-[14px] text-muted leading-relaxed">
                      Set up or update your profile in the Max &amp; Sherry
                      mobile app.
                    </p>
                  </div>
                </Reveal>
              )}
            </section>

            <section>
              <Reveal delay={250}>
                <h2 className="text-base md:text-xl font-bold tracking-tight mb-3 md:mb-4">
                  Danger zone
                </h2>
                <div className="p-4 md:p-6 rounded-2xl border border-rose/20 bg-blush/40 flex flex-col gap-3 md:gap-4">
                  <div>
                    <p className="text-[14px] font-semibold text-black mb-1">
                      Delete your account
                    </p>
                    <p className="text-[13px] text-muted leading-relaxed">
                      Permanently remove your profile, matches, messages, and
                      data.
                    </p>
                  </div>
                  <Link
                    to="/delete-account"
                    className="self-start text-[13px] font-semibold text-rose-dark border border-rose/30 px-5 py-2.5 rounded-full hover:bg-rose/10 transition-colors whitespace-nowrap no-underline"
                  >
                    Delete account
                  </Link>
                </div>
              </Reveal>
            </section>
          </div>
        </div>
      </div>

      {/* ---------- Cancel subscription modal ---------- */}
      {showCancelModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={() => !cancelling && setShowCancelModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-[440px] w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold tracking-tight mb-2">
              Cancel subscription?
            </h3>
            <p className="text-[14px] text-muted leading-relaxed mb-5">
              You'll keep access until the end of your current billing period.
              Tell us why you're leaving (optional):
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Too expensive, not using it enough, etc."
              className="w-full px-4 py-3 rounded-2xl border border-black/10 text-sm outline-none focus:border-rose transition-colors mb-4 resize-none"
            />
            {cancelError && (
              <p className="text-[13px] text-rose-dark mb-3">{cancelError}</p>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                disabled={cancelling}
                className="flex-1 text-[13px] font-semibold text-black border border-black/10 py-3 rounded-full hover:bg-neutral-50 transition-colors disabled:opacity-40"
              >
                Keep subscription
              </button>
              <button
                type="button"
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="flex-1 text-[13px] font-semibold text-white bg-rose-dark py-3 rounded-full hover:opacity-90 transition-opacity disabled:opacity-40"
              >
                {cancelling ? "Cancelling…" : "Yes, cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Payments popup ---------- */}
      {paymentsModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={() => setPaymentsModal(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-[480px] w-full max-h-[80vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-black/5">
              <h3 className="text-base font-bold tracking-tight">
                {paymentsModal === "failed"
                  ? "Failed payments"
                  : "Payment history"}
              </h3>
              <button
                type="button"
                onClick={() => setPaymentsModal(null)}
                className="text-[13px] font-semibold text-muted hover:text-black transition-colors"
              >
                Close
              </button>
            </div>
            <div className="overflow-y-auto">
              {(paymentsModal === "failed"
                ? failedPayments
                : otherPayments
              ).map(paymentRow)}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
